import { ActionWithMoreInfoModel } from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import { Subject } from 'rxjs';
import { RequestModel } from '@bo-client/shared/models/request.model';
import { ShowErrorModel } from '@bo-client/shared/form-errors/errors-form-model';
import { RequestFormBase } from '@bo-client/shared/base-components/request-form.base';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { RequestApprovalDto } from '@bo-client/activation-management/grid-registration/model/RequestApprovalDto';
import {
  AnswerStateEnum,
  AnswerWithMoreInfoModel,
  NavigateToEnum
} from '@bo-client/activation-management/shared/models/AnswerWithMoreInfoModel';
import { path } from '@bo-client/shared/constants/path';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatDialogSize } from '@bo-client/shared/constants/mat-dialog-size';
import { ConfimWithCommentModalComponent } from '@bo-client/activation-management/commissioning/forms/components/modal-validate-commissioning/confim-with-comment-modal.component';

export abstract class ConfirmationRequestPageBase<T extends RequestModel> {
  abstract uriGetData: string;
  compUnsubs$ = new Subject<void>();
  abstract requestApprovalDto: RequestApprovalDto;

  public title: any;
  public form: RequestFormBase<T>;
  public errorFormList: ShowErrorModel[] = [];
  public model: T = null;

  public id: string = this.route.snapshot.paramMap.get('id');
  public token: string = this.route.snapshot.paramMap.get('token');

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected httpService: HttpHapService,
    protected toastService: ToastrService,
    protected matDialog: MatDialog
  ) {}

  init() {
    this.httpService
      .getTyped<AnswerWithMoreInfoModel<T>>(this.uriGetData + '/' + this.id + '/' + this.token)
      .pipe(takeUntil(this.compUnsubs$))
      .subscribe((answer) => {
        this.getApiResponse('get', answer);
      });
  }

  onFormErrorsChanged(formErrorChangedEvent: any) {
    this.errorFormList = formErrorChangedEvent;
  }

  actionToRun(action: ActionWithMoreInfoModel) {
    if (action.validateForm) {
      if (!this.form.isValid()) {
        return;
      }
    }
    this[action.execute](action);
  }

  executeAction(actionInfo: ActionWithMoreInfoModel) {
    console.log('Executing action conf re base', actionInfo, this.form);
    this.requestApprovalDto = {
      id: this.model.id,
      token: this.token,
      additionalData: this.form.getFormGroup().value
    };

    // TODO: improve wording
    this.hookGetDataFromForm();
    if (actionInfo.validateForm) {
      if (!this.form.isValid()) {
        return;
      }
    }

    this.httpService
      .post<AnswerWithMoreInfoModel<T>>(this.requestApprovalDto, actionInfo.actionUrl)
      .pipe(takeUntil(this.compUnsubs$))
      .subscribe(
        (answer) => {
          this.getApiResponse('post', answer, actionInfo);
        },
        (error) => {
          this.displayErrorMessage(error, actionInfo.failureMessage);
        }
      );
  }

  confirmActionWithComment(actionInfo: ActionWithMoreInfoModel) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.id = 'commission-request-activity';
    dialogConfig.maxHeight = MatDialogSize.maxHeightLogin;
    dialogConfig.width = MatDialogSize.width;
    dialogConfig.data = actionInfo;

    this.matDialog
      .open(ConfimWithCommentModalComponent, dialogConfig)
      .afterClosed()
      .pipe(takeUntil(this.compUnsubs$))
      .subscribe((res: ActionWithMoreInfoModel) => {
        if (res) {
          this.actionPlusMail(res);
          console.log(res);
        }
      });
  }

  actionPlusMail(actionInfo: ActionWithMoreInfoModel) {
    this.requestApprovalDto = {
      id: this.model.id,
      token: this.token,
      subject: actionInfo.subject,
      comment: actionInfo.comment,
      additionalData: { formConfirmation: false }
    };
    this.httpService
      .post<AnswerWithMoreInfoModel<T>>(this.requestApprovalDto, actionInfo.actionUrl)
      .pipe(takeUntil(this.compUnsubs$))
      .subscribe(
        (answer) => {
          this.getApiResponse('post', answer, actionInfo);
          this.toastService.success(actionInfo.successMessage);
        },
        (error) => {
          this.displayErrorMessage(error, actionInfo.failureMessage);
        }
      );
  }

  checkIfErrors(actionInfo: ActionWithMoreInfoModel) {
    return;
  }

  displayErrorMessage(error: any, defaultMessage: string) {
    if (error.error.message) {
      this.toastService.error(error.error.message);
    } else {
      this.toastService.error(defaultMessage);
    }
  }

  getApiResponse(
    apiMethod: string,
    answer: AnswerWithMoreInfoModel<T>,
    actionInfo?: ActionWithMoreInfoModel
  ) {
    switch (apiMethod) {
      case 'get': {
        switch (answer.answerState) {
          case AnswerStateEnum.OK:
            this.model = answer.entity;

            // we have to check this because there are cases in which we dont have a form
            if (this.form) {
              this.form.loadFormWithModel(this.model);
            }
            this.hookDataLoaded(answer);
            break;
          case AnswerStateEnum.ERROR:
            this.showMessage(answer);
            this.navigateTo(answer);
            break;
        }
        break;
      }
      case 'post': {
        this.showMessage(answer, actionInfo);
        this.navigateTo(answer);
        break;
      }
    }
  }

  showMessage(answer: AnswerWithMoreInfoModel<T>, actionInfo?: ActionWithMoreInfoModel) {
    switch (answer.answerState) {
      case AnswerStateEnum.OK:
        if (answer.message) {
          this.toastService.success(answer.message);
          return;
        }
        if (actionInfo) {
          this.toastService.success(actionInfo.successMessage);
          return;
        }
        break;
      case AnswerStateEnum.ERROR:
        if (answer.message) {
          this.toastService.error(answer.message);
          return;
        }
        if (actionInfo) {
          this.toastService.error(actionInfo.failureMessage);
          return;
        }
        break;
    }
  }

  navigateTo(answer: AnswerWithMoreInfoModel<T>) {
    if (answer.navigateTo !== NavigateToEnum.none) {
      this.router.navigate([path.external + '/' + answer.navigateTo + '/' + answer.message], {
        replaceUrl: true
      });
    }
  }

  protected hookDataLoaded(answer: AnswerWithMoreInfoModel<T>) {}

  protected hookGetDataFromForm() {}
}
